/**
 * The asset manifest — the contract between the offline conversion tools (`tools/convert/`)
 * and everything that loads assets at runtime.
 *
 * WHY THIS EXISTS AT ALL: the original discovers its animation set by scanning a directory
 * at startup (`animcollection.cpp:387,426`), and its advertising boards the same way
 * (`match.cpp:533`). A browser cannot list a directory. These generated indexes replace
 * that scan, so nothing loads without them.
 *
 * Kit and badge filenames are built by string concatenation from database values
 * (`team.cpp:88,560`), so the texture index must be produced by querying the database, not
 * by walking the source tree.
 *
 * Paths are relative to the asset root and always use forward slashes. Keys are the
 * ORIGINAL asset paths (e.g. "media/objects/balls/generic.ase") so that code ported from
 * the C++ can look assets up by the name the original used.
 */

export const MANIFEST_VERSION = 1;

// ---------------------------------------------------------------------------

export interface MeshEntry {
  /** Converted file, relative to the asset root. glTF binary. */
  file: string;
  /** Draw calls after material merging. The original's stadium had 1,642; see PLAN §3.4. */
  primitiveCount: number;
  vertexCount: number;
  triangleCount: number;
  /** Texture keys this mesh references, resolved against `textures`. */
  textures: string[];
}

export interface TextureEntry {
  file: string;
  width: number;
  height: number;
  hasAlpha: boolean;
  /** True when the original referenced this texture but no file existed on disk.
   *  Two such references exist (`assets.md` §1). Must not be fatal. */
  missing?: boolean;
}

export interface AudioEntry {
  file: string;
  durationMs: number;
  channels: number;
  sampleRate: number;
}

/**
 * One animation clip, as much as the boot-time manifest needs to know about it: which file
 * its bytes live in and how many frames it has.
 *
 * The original's per-clip variables (parsed from the trailing XML block, `animation.cpp:
 * 1143-1148`) are NOT here. They used to be duplicated into this manifest as well as into
 * `animations/index.json`, at a cost of about 500 KB on every load; nothing actually read
 * the copy here, so it was dropped. `animations/index.json` — loaded via
 * `AssetStore.animationIndex()` and turned into the runtime clip library by `loadLibrary`
 * (`src/sim/anim/clip.ts`) — is the one place `vars` lives now.
 */
export interface AnimationEntry {
  /** Key: original path relative to the animation directory, without extension. */
  name: string;
  file: string;
  frameCount: number;
}

export interface SkeletonEntry {
  /**
   * Converted from player.object — the player skeleton, 14 joints.
   * (docs/original-engine/assets.md originally said 17; the converted data, read from the
   * file itself, has 14. The doc has been corrected.)
   */
  file: string;
  jointNames: string[];
  /** Rest pose, converted from the `.util` reference-pose files. */
  restPoseFile: string;
  /** All three reference poses, keyed by the original's path. See core/pose.ts for what
   *  each is for — two of them are what binds the body mesh to the skeleton. */
  restPoseFiles: Record<string, string>;
}

/**
 * Bone weights for a mesh that deforms with the skeleton.
 *
 * There is exactly one of these: the player's body. The original does not store weights at
 * all — it hides them in the mesh's VERTEX COLOURS and recovers them by parsing the mesh file
 * a second time (`gamedefines.cpp:14`, decoded at `humanoidbase.cpp:315-345`). The offline
 * step that undoes that is tools/convert/skinning.py, which explains the encoding.
 */
export interface SkinningEntry {
  file: string;
  /** Mesh key this skin belongs to, e.g. "media/objects/players/models/fullbody.ase". */
  mesh: string;
  bonesPerVertex: number;
  vertexCount: number;
}

export interface DatabaseIndex {
  file: string;
  teamCount: number;
  playerCount: number;
}

// ---------------------------------------------------------------------------

export interface AssetManifest {
  version: number;
  /** ISO timestamp of the conversion run, for cache-busting and provenance. */
  generated: string;
  /** Total bytes of all converted assets, for load-progress reporting. */
  totalBytes: number;

  meshes: Record<string, MeshEntry>;
  textures: Record<string, TextureEntry>;
  audio: Record<string, AudioEntry>;
  /** Array, not a map: the original's animation set is ordered by directory scan and the
   *  selection search iterates it. Order is part of the contract. */
  animations: AnimationEntry[];
  skeleton: SkeletonEntry;
  /** Keyed by a short name ("fullbody"), not by asset path — there is one per skinned mesh. */
  skinning: Record<string, SkinningEntry>;
  database: DatabaseIndex;

  /** Advertising board textures, replacing the runtime directory scan at match.cpp:533. */
  adboards: string[];

  /**
   * Assets required before the first frame can be drawn — about 1.43 MB converted.
   *
   * (An earlier estimate here said ~500 KB. That was wrong: it omitted the menu backdrop
   * and the kit and badge art. Measured, not estimated.)
   *
   * NOTE: 14 of the current entries — thirteen shader files and one font — are not produced
   * by any conversion stream, because shaders are ported into src/render/shaders/ as source
   * and fonts are handled by the browser. The loader warns and continues for these rather
   * than failing; see loader.ts preloadBootSet.
   */
  bootSet: string[];
}

export function isManifest(value: unknown): value is AssetManifest {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Partial<AssetManifest>;
  return (
    m.version === MANIFEST_VERSION &&
    typeof m.meshes === 'object' &&
    typeof m.textures === 'object' &&
    Array.isArray(m.animations) &&
    Array.isArray(m.adboards)
  );
}
